import { type SortingState, type VisibilityState } from '@tanstack/react-table';

import { type GetAllClassParams } from '~entities/classes/api/get-all-classes-with-search';
import {
  type ClassListView,
  type SaveClassListViewParams,
} from '~entities/studios/api/class-list-view';

import { mergeClassListOrder } from './class-list-column-prefs';

/** All Classes column id → class search `sortBy` key. */
export const CLASS_SORT_KEY_MAP: Record<string, string> = {
  title: 'title',
  Session: 'sessionName',
  days: 'daysString',
  'Location Name': 'locationName',
  Room: 'roomName',
  'Age (Range)': 'ageRangeStart',
  Instructor: 'instructorNames',
  Time: 'startTime',
  'Start Date': 'startDate',
  'End Date': 'endDate',
  'Number of Seats': 'maxSize',
  'Tuition Fee': 'tuitionFee',
  Tags: 'tagsString',
};

export const sortingFromClassListView = (
  view: ClassListView | null | undefined,
): SortingState => {
  if (!view?.sortBy) return [];
  const columnId = Object.keys(CLASS_SORT_KEY_MAP).find(
    (id) => CLASS_SORT_KEY_MAP[id] === view.sortBy,
  );
  return columnId ? [{ id: columnId, desc: view.sortOrder === 'desc' }] : [];
};

export const classSearchSortFromSorting = (
  sorting: SortingState,
): Pick<GetAllClassParams, 'sortBy' | 'sortOrder'> => {
  const primary = sorting[0];
  const sortBy = primary
    ? (CLASS_SORT_KEY_MAP[primary.id] ?? primary.id)
    : undefined;
  return {
    sortBy,
    sortOrder: sortBy ? (primary?.desc ? 'desc' : 'asc') : undefined,
  };
};

/**
 * Class search params for an All Classes load with default filters. Key order
 * matches the table's query so the route loader prefetch hydrates it.
 */
export const defaultClassListSearchParams = (
  view: ClassListView | null,
  pageSize: number,
): GetAllClassParams => ({
  name: '',
  page: 1,
  limit: pageSize,
  studio: true,
  showPastClasses: false,
  isArchive: false,
  enforceStatusFilters: true,
  ...classSearchSortFromSorting(sortingFromClassListView(view)),
});

export const visibilityFromClassListView = (
  view: ClassListView,
  defaults: VisibilityState,
): VisibilityState => {
  const next = { ...defaults };
  for (const id of view.hiddenColumns) {
    if (id in next) next[id] = false;
  }
  return next;
};

export const orderFromClassListView = (
  view: ClassListView,
  columnIds: Array<string>,
): Array<string> => mergeClassListOrder(view.columnOrder, columnIds);

/** Only sorts the class search API accepts are saved; others save unsorted. */
export const classListViewFromState = ({
  sorting,
  visibility,
  order,
}: {
  sorting: SortingState;
  visibility: VisibilityState;
  order: Array<string>;
}): SaveClassListViewParams => {
  const primary = sorting[0];
  const sortBy = primary ? (CLASS_SORT_KEY_MAP[primary.id] ?? null) : null;
  return {
    sortBy,
    sortOrder: sortBy && primary?.desc ? 'desc' : 'asc',
    hiddenColumns: Object.keys(visibility).filter((id) => !visibility[id]),
    columnOrder: order,
  };
};
